/**
 * QuickStart information for Weave product.
 */

import {TargetBlank} from '@wandb/weave/common/util/links';
import {ButtonSizes} from '@wandb/weave/components/Button';
import {IconNames} from '@wandb/weave/components/Icon';
import {ToggleButtonGroup} from '@wandb/weave/components/ToggleButtonGroup';
import React, {useCallback, useEffect, useState} from 'react';

import {QuickStartAnalyticsOrigin} from '../../../services/analytics/quickStartEvents';
import * as S from '../QuickStart.styles';
import {Product} from '../QuickStartProductSelect';
import {QuickStartWeaveCode} from './QuickStartWeaveCode';
import {QuickStartWeavePlayground} from './QuickStartWeavePlayground';

// Define constants for tracking keys only
const WEAVE_CODE_KEY = 'weave-code';
const WEAVE_PLAYGROUND_KEY = 'weave-playground';

type QuickStartWeaveProps = {
  entityName: string;
  projectName: string;
  analyticsOrigin: QuickStartAnalyticsOrigin;
  maxWidth: number;
  onVariantActivated: (product: Product, variant: string) => void;
};

type LayoutOptionValue = 'Playground' | 'Code';

export const QuickStartWeave = ({
  entityName,
  projectName,
  analyticsOrigin,
  maxWidth,
  onVariantActivated,
}: QuickStartWeaveProps) => {
  const [selectedLayout, setSelectedLayout] =
    useState<LayoutOptionValue>('Playground');

  useEffect(() => {
    onVariantActivated(
      Product.Weave,
      selectedLayout === 'Playground' ? WEAVE_PLAYGROUND_KEY : WEAVE_CODE_KEY
    );
  }, [onVariantActivated, selectedLayout]);

  const onToggleSelection = useCallback((value: string) => {
    setSelectedLayout(value as LayoutOptionValue);
  }, []);

  return (
    <>
      <S.QuickStartHeader>
        <S.QuickStartHeaderText>Get started with Weave</S.QuickStartHeaderText>
        <p>Weave is a toolkit for developing AI-powered applications.</p>
        <div>
          You can use Weave to:
          <ul>
            <li>Log and debug language model inputs, outputs, and traces</li>
            <li>
              Build rigorous, apples-to-apples evaluations for language model
              use cases
            </li>
            <li>
              Organize all the information generated across the LLM workflow,
              from experimentation to evaluations to production
            </li>
          </ul>
          Start by logging a trace—either from the playground or right from your
          code. Visit{' '}
          <TargetBlank href="https://wandb.me/weave">
            our documentation
          </TargetBlank>{' '}
          to learn more.
        </div>
      </S.QuickStartHeader>
      <ToggleButtonGroup
        className="rounded-lg"
        options={[
          {
            value: 'Playground',
            icon: IconNames.SandboxPlayground,
          },
          {
            value: 'Code',
            icon: IconNames.CodeAlt,
          },
        ]}
        value={selectedLayout}
        size={ButtonSizes.Small}
        onValueChange={onToggleSelection}
      />
      {selectedLayout === 'Playground' && (
        <QuickStartWeavePlayground
          entityName={entityName}
          projectName={projectName}
          analyticsOrigin={analyticsOrigin}
        />
      )}
      {selectedLayout === 'Code' && (
        <QuickStartWeaveCode
          entityName={entityName}
          projectName={projectName}
          analyticsOrigin={analyticsOrigin}
          maxWidth={maxWidth}
        />
      )}
    </>
  );
};
